import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, Activity } from 'lucide-react';

const MarketData = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Market Overview</h2>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading market data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Categorize cryptocurrencies by tiers
  const tiers = {
    'Tier 1 - Premium Assets': ['BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'SOL/USDT', 'ADA/USDT'],
    'Tier 2 - Growth Assets': ['AVAX/USDT', 'DOT/USDT', 'LINK/USDT', 'UNI/USDT', 'LTC/USDT'],
    'Tier 3 - Opportunity Assets': ['XRP/USDT', 'MATIC/USDT', 'ATOM/USDT', 'FTM/USDT', 'NEAR/USDT'],
    'Tier 4 - Speculative Assets': ['DOGE/USDT', 'SHIB/USDT', 'TRX/USDT', 'XLM/USDT', 'VET/USDT']
  };

  const tierConfigs = {
    'Tier 1 - Premium Assets': { color: 'bg-yellow-500/10 border-yellow-500/30', leverage: '5-10x' },
    'Tier 2 - Growth Assets': { color: 'bg-green-500/10 border-green-500/30', leverage: '8-12x' },
    'Tier 3 - Opportunity Assets': { color: 'bg-blue-500/10 border-blue-500/30', leverage: '10-15x' },
    'Tier 4 - Speculative Assets': { color: 'bg-purple-500/10 border-purple-500/30', leverage: '12-25x' }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-white mb-6">Market Overview</h2>
      
      {Object.entries(tiers).map(([tierName, symbols]) => {
        const tierData = data.filter(item => symbols.includes(item.symbol));
        
        if (tierData.length === 0) return null;

        return (
          <div key={tierName} className={`mb-8 rounded-lg border p-4 ${tierConfigs[tierName].color}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">{tierName}</h3>
              <span className="text-sm text-gray-400">Leverage: {tierConfigs[tierName].leverage}</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {tierData.map((item) => (
                <div key={item.symbol} className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white">
                      {item.symbol.replace('/USDT', '')}
                    </span>
                    {item.change_24h >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-400" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center">
                      <DollarSign className="w-3 h-3 text-gray-400 mr-1" />
                      <span className="text-white font-medium">
                        ${item.price.toLocaleString()}
                      </span>
                    </div>
                    
                    <div className={`text-sm ${item.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {item.change_24h >= 0 ? '+' : ''}{item.change_24h.toFixed(2)}%
                    </div>
                    
                    <div className="flex items-center text-xs text-gray-400">
                      <Activity className="w-3 h-3 mr-1" />
                      Vol: ${(item.volume / 1000000).toFixed(1)}M
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      H: ${item.high_24h.toLocaleString()} L: ${item.low_24h.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      
      <div className="mt-6 p-4 bg-blue-900/20 rounded-lg border border-blue-800">
        <div className="flex items-center mb-2">
          <Activity className="w-5 h-5 text-blue-400 mr-2" />
          <span className="text-blue-300 font-medium">Trading Strategy</span>
        </div>
        <p className="text-blue-200 text-sm">
          Min/Max 10-minute strategy with trailing stop 0.5% • TP: 15% • SL: 50% • Adaptive leverage per tier
        </p>
      </div>
    </div>
  );
};

export default MarketData;